import { useEffect, useRef } from "react";
import { AiOutlineUser } from "react-icons/ai";
import { MdOutlineFavoriteBorder } from "react-icons/md";
import { useParams } from "react-router-dom";
import styled, { css } from "styled-components";
import tw from "twin.macro";
import { apiRoutes } from "../../api";
import browserRoutes from "../../common/browserRoutes";
import { DP, IPage } from "../../common/types";
import SpinnerDots from "../../components/SpinnerDots";
import { createCustomStore } from "../../context/customStore";
import { IPost } from "../../sections/Post/types";
import Navigation from "./components/Navigation";
import Posts from "./components/Posts";
import UserInfo from "./components/UserInfo";
import { IUserDetails } from "./types";

interface Props extends DP {}

export type PagesType = "user" | "favorite";
export type PostParam = "withEdit" | "withAuthor";

interface UserDetailsPage extends IPage {
  postParams: PostParam[];
}

export const pages: Record<PagesType, UserDetailsPage> = {
  user: {
    title: "Posts",
    icon: AiOutlineUser,
    location: (u) => browserRoutes.user(u),
    apiRoute: apiRoutes.getUserPosts,
    postParams: [],
  },
  favorite: {
    title: "Favorite",
    icon: MdOutlineFavoriteBorder,
    location: (u) => browserRoutes.userLikes(u),
    apiRoute: apiRoutes.getFavoritePosts,
    postParams: ["withAuthor"],
  },
};

interface IStore {
  posts: IPost[];
  user?: IUserDetails;
  username?: string;
  isPostsLoading: boolean;
  isUserInfoLoading: boolean;
  isSubscribeButtonLoading: boolean;
  currentPage?: PagesType;
  isDescriptionEditing: boolean;
	descInput: string;
	isDescEditFormLoading: boolean,
	offset: number,
	totalPages?: number,
	page: number,
	isFetching: boolean
}

export const {
  useValue: useUserDetailsStore,
  set: setUserDetailsStore,
  get: getUserDetailsStore,
  setOne: setUserDetailsStoreOne,
} = createCustomStore<IStore>({
	posts: [],
  isPostsLoading: true,
  isUserInfoLoading: true,
  isSubscribeButtonLoading: false,
  // currentPage: "user",
  isDescriptionEditing: false,
  descInput: "",
  isDescEditFormLoading: false,
	offset: 6,
	page: 0,
	isFetching: true
});

const UserDetails = (props: Props) => {
  const { username } = useParams();
  setUserDetailsStore({ username });

	const isFetching = useUserDetailsStore((s) => s.isFetching);
	const isPostsLoading = useUserDetailsStore((s) => s.isPostsLoading)
	const { page, totalPages } = getUserDetailsStore();

	const wrapperRef = useRef<HTMLDivElement | null>(null);

	const handleScroll = (e) => {
		const scrollBottom = e.target.scrollTop + window.innerHeight;
		if (
			e.target.scrollHeight - scrollBottom < 100 &&
			!isFetching &&
			page < (totalPages ?? 2)
		) {
			setUserDetailsStoreOne("page", (v) => v + 1);
			setUserDetailsStore({ isFetching: true });
		}
	};

    useEffect(() => {
      if (wrapperRef.current) {
        wrapperRef.current.addEventListener("scroll", handleScroll);
      }
      return () => {
        if (wrapperRef.current)
          wrapperRef.current.removeEventListener("scroll", handleScroll);
      };
    }, [wrapperRef.current, isFetching, isPostsLoading]);

  return (
    <Wrapper ref={wrapperRef} className={props.className}>
      <UserInfo />
      <Navigation />
      <Posts />
			{isFetching && <SpinnerDots />}
    </Wrapper>
  );
};

const Wrapper = styled.div(() => [
  tw`flex flex-col items-center w-full overflow-y-auto gap-y-4`,
  css`
    height: calc(var(--content-h));

    & > * {
      ${tw`w-7/12`}
    }

    ${SpinnerDots} {
			${tw`mx-auto mt-2 mb-4 w-min`}
		}
  `,
]);

export default styled(UserDetails)``;
